import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Upload, Download, FileSpreadsheet, Users, UserPlus, AlertCircle, CheckCircle, FileText, Loader2, GraduationCap, Briefcase } from 'lucide-react';
import { canEdit } from '@/lib/auth';
import api from '@/lib/api';

export default function ImportExport() {
  const [file, setFile] = useState(null);
  const [teacherFile, setTeacherFile] = useState(null);
  const [equipeDnaFile, setEquipeDnaFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importingTeachers, setImportingTeachers] = useState(false);
  const [importingEquipeDna, setImportingEquipeDna] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingTeachers, setDownloadingTeachers] = useState(false);
  const [downloadingEquipeDna, setDownloadingEquipeDna] = useState(false);
  const [result, setResult] = useState(null);
  const [teacherResult, setTeacherResult] = useState(null);
  const [equipeDnaResult, setEquipeDnaResult] = useState(null);

  const handleImport = async (e) => {
    e.preventDefault();
    if (!file) return;
    
    setImporting(true);
    setResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/import/excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setFile(null);
        // Reset file input
        const fileInput = document.getElementById('excel-file-input');
        if (fileInput) fileInput.value = '';
      } else {
        toast.error('Verifique os erros na importação');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
      setResult({
        success: false,
        message: error.response?.data?.detail || 'Erro ao importar arquivo',
        responsibles: { imported: 0, errors: [] },
        students: { imported: 0, errors: [] }
      });
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await api.get('/import/template', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_importacao.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao baixar template');
    } finally {
      setDownloading(false);
    }
  };

  const downloadTeacherTemplate = async () => {
    setDownloadingTeachers(true);
    try {
      const response = await api.get('/import/template/teachers', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_professores.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template de professores baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao baixar template');
    } finally {
      setDownloadingTeachers(false);
    }
  };

  const handleImportTeachers = async (e) => {
    e.preventDefault();
    if (!teacherFile) return;
    
    setImportingTeachers(true);
    setTeacherResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', teacherFile);
      
      const response = await api.post('/import/teachers', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setTeacherResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setTeacherFile(null);
        const fileInput = document.getElementById('teacher-file-input');
        if (fileInput) fileInput.value = '';
      } else {
        toast.error('Verifique os erros na importação');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar professores');
      setTeacherResult({
        success: false,
        message: error.response?.data?.detail || 'Erro ao importar professores',
        imported: 0,
        errors: []
      });
    } finally {
      setImportingTeachers(false);
    }
  };

  // Equipe DNA functions
  const downloadEquipeDnaTemplate = async () => {
    setDownloadingEquipeDna(true);
    try {
      const response = await api.get('/import/template/equipe-dna', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_equipe_dna.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template da Equipe DNA baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao baixar template');
    } finally {
      setDownloadingEquipeDna(false);
    }
  };

  const handleImportEquipeDna = async (e) => {
    e.preventDefault();
    if (!equipeDnaFile) return;
    
    setImportingEquipeDna(true);
    setEquipeDnaResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', equipeDnaFile);
      
      const response = await api.post('/import/equipe-dna', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setEquipeDnaResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setEquipeDnaFile(null);
        const fileInput = document.getElementById('equipe-dna-file-input');
        if (fileInput) fileInput.value = '';
      } else {
        toast.error('Verifique os erros na importação');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar equipe');
      setEquipeDnaResult({
        success: false,
        message: error.response?.data?.detail || 'Erro ao importar equipe',
        teachers: { imported: 0, updated: 0, errors: [] },
        staff: { imported: 0, updated: 0, errors: [] }
      });
    } finally {
      setImportingEquipeDna(false);
    }
  };

  if (!canEdit()) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Você não tem permissão para importar dados.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="import-export-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Importar Dados</h1>
        <p className="text-muted-foreground mt-1">Importação em massa via Excel</p>
      </div>

      <Tabs defaultValue="equipe-dna" className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-2xl">
          <TabsTrigger value="equipe-dna" className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            Equipe DNA
          </TabsTrigger>
          <TabsTrigger value="students" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Alunos e Responsáveis
          </TabsTrigger>
          <TabsTrigger value="teachers" className="flex items-center gap-2">
            <GraduationCap className="w-4 h-4" />
            Professores (Legado)
          </TabsTrigger>
        </TabsList>

        {/* Tab Equipe DNA - NOVA */}
        <TabsContent value="equipe-dna" className="space-y-6 mt-6">
          {/* Resultado da Importação Equipe DNA */}
          {equipeDnaResult && (
            <Alert className={equipeDnaResult.success ? 'border-green-500 bg-green-50' : 'border-amber-500 bg-amber-50'}>
              {equipeDnaResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-amber-600" />
              )}
              <AlertDescription>
                <p className="font-semibold mb-3">{equipeDnaResult.message}</p>
                
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="bg-white/50 p-3 rounded-md">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-purple-600" /> Professores
                    </p>
                    <p className="text-2xl font-bold text-purple-600">
                      {(equipeDnaResult.teachers?.imported || 0) + (equipeDnaResult.teachers?.updated || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {equipeDnaResult.teachers?.imported || 0} novos, {equipeDnaResult.teachers?.updated || 0} atualizados
                    </p>
                  </div>
                  <div className="bg-white/50 p-3 rounded-md">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-blue-600" /> Equipe Escolar
                    </p>
                    <p className="text-2xl font-bold text-blue-600">
                      {(equipeDnaResult.staff?.imported || 0) + (equipeDnaResult.staff?.updated || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {equipeDnaResult.staff?.imported || 0} novos, {equipeDnaResult.staff?.updated || 0} atualizados
                    </p>
                  </div>
                </div>
                
                {/* Erros de Professores */}
                {equipeDnaResult.teachers?.errors?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-amber-700">Avisos - Professores:</p>
                    <ul className="list-disc list-inside text-sm mt-1 max-h-32 overflow-y-auto">
                      {equipeDnaResult.teachers.errors.map((error, idx) => (
                        <li key={idx} className="text-amber-600">{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Erros de Equipe Escolar */}
                {equipeDnaResult.staff?.errors?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-700">Erros - Equipe Escolar:</p>
                    <ul className="list-disc list-inside text-sm mt-1 max-h-32 overflow-y-auto">
                      {equipeDnaResult.staff.errors.map((error, idx) => (
                        <li key={idx} className="text-red-600">{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Card de Upload Equipe DNA */}
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-purple-600" />
                  Importar Equipe DNA
                </CardTitle>
                <CardDescription>
                  Importe professores e equipe escolar de uma só vez
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-md border border-purple-200">
                  <p className="text-sm font-medium text-purple-800 mb-2">🎯 Separação Automática</p>
                  <p className="text-xs text-purple-700">
                    O sistema identifica automaticamente quem é <strong>Professor</strong> (cargo contém "Professor") 
                    e quem é <strong>Equipe Escolar</strong> (Secretário, Monitor, Auxiliar, etc.)
                  </p>
                </div>

                <Button
                  variant="outline"
                  onClick={downloadEquipeDnaTemplate}
                  disabled={downloadingEquipeDna}
                  className="w-full"
                >
                  {downloadingEquipeDna ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Baixar Template Equipe DNA
                </Button>

                <form onSubmit={handleImportEquipeDna} className="space-y-4">
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-purple-500/50 transition-colors">
                    <Input
                      id="equipe-dna-file-input"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setEquipeDnaFile(e.target.files[0])}
                      className="hidden"
                    />
                    <label htmlFor="equipe-dna-file-input" className="cursor-pointer">
                      <Briefcase className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm font-medium">
                        {equipeDnaFile ? equipeDnaFile.name : 'Clique para selecionar arquivo'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Apenas arquivos .xlsx
                      </p>
                    </label>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={!equipeDnaFile || importingEquipeDna}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  >
                    {importingEquipeDna ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Importar Equipe DNA
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Instruções Equipe DNA */}
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-blue-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Como Funciona
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 text-sm">
                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                    <div>
                      <p className="font-medium">Baixe o template</p>
                      <p className="text-muted-foreground">O template já está no formato da sua planilha atual.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                    <div>
                      <p className="font-medium">Preencha os dados</p>
                      <p className="text-muted-foreground">Coloque todos os funcionários na mesma planilha.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold">3</span>
                    <div>
                      <p className="font-medium">Faça o upload</p>
                      <p className="text-muted-foreground">O sistema separa automaticamente professores de equipe escolar.</p>
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t space-y-2">
                    <p className="font-medium text-purple-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> Credenciais de Acesso:
                    </p>
                    <div className="bg-white/80 p-3 rounded-md space-y-1 text-xs">
                      <p><strong>Login:</strong> CPF (apenas números) ou E-mail</p>
                      <p><strong>Senha:</strong> novodna@2026</p>
                      <p className="text-muted-foreground mt-2">
                        Se não informar e-mail, será gerado automaticamente (ex: nome.sobrenome@dna.escola.com)
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Campos do Template Equipe DNA */}
          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="text-lg">Campos do Template</CardTitle>
              <CardDescription>Todos os 25 campos disponíveis para importação</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-purple-600"></span>
                    Dados Pessoais
                  </h4>
                  <div className="bg-muted p-3 rounded-md space-y-1 text-xs font-mono">
                    <p className="text-red-600 font-bold">NOME COMPLETO*</p>
                    <p>CPF</p>
                    <p>RG</p>
                    <p>Data Nascimento</p>
                    <p>Estado Civil</p>
                    <p>Gênero</p>
                    <p>Nome (apelido)</p>
                    <p>Cidade Natal</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-blue-600"></span>
                    Endereço e Contato
                  </h4>
                  <div className="bg-muted p-3 rounded-md space-y-1 text-xs font-mono">
                    <p>Endereço</p>
                    <p>Bairro</p>
                    <p>Cidade</p>
                    <p>CEP</p>
                    <p>Fone</p>
                    <p>Celular</p>
                    <p>Fone Residencial</p>
                    <p>E-mail</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-600"></span>
                    Dados Profissionais e Financeiro
                  </h4>
                  <div className="bg-muted p-3 rounded-md space-y-1 text-xs font-mono">
                    <p className="text-red-600 font-bold">CARGO*</p>
                    <p>Nº Matrícula</p>
                    <p>Data Admissão</p>
                    <p>Carteira Profissional</p>
                    <p>PIS/PASEP</p>
                    <p>INSS</p>
                    <p>Nº Dependentes</p>
                    <p>Currículo</p>
                    <p className="text-green-600 font-semibold">PIX (Chave)</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Alunos e Responsáveis */}
        <TabsContent value="students" className="space-y-6 mt-6">
          {/* Resultado da Importação */}
          {result && (
        <Alert className={result.success ? 'border-green-500 bg-green-50' : 'border-amber-500 bg-amber-50'}>
          {result.success ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-amber-600" />
          )}
          <AlertDescription>
            <p className="font-semibold mb-3">{result.message}</p>
            
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div className="bg-white/50 p-3 rounded-md">
                <p className="text-sm font-medium flex items-center gap-2">
                  <UserPlus className="w-4 h-4" /> Responsáveis
                </p>
                <p className="text-2xl font-bold text-green-600">{result.responsibles?.imported || 0}</p>
                <p className="text-xs text-muted-foreground">importados</p>
              </div>
              <div className="bg-white/50 p-3 rounded-md">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Users className="w-4 h-4" /> Alunos
                </p>
                <p className="text-2xl font-bold text-blue-600">{result.students?.imported || 0}</p>
                <p className="text-xs text-muted-foreground">importados</p>
              </div>
            </div>
            
            {/* Erros de Responsáveis */}
            {result.responsibles?.errors?.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-medium text-amber-700">Avisos - Responsáveis:</p>
                <ul className="list-disc list-inside text-sm mt-1 max-h-32 overflow-y-auto">
                  {result.responsibles.errors.map((error, idx) => (
                    <li key={idx} className="text-amber-600">{error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Erros de Alunos */}
            {result.students?.errors?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-red-700">Erros - Alunos:</p>
                <ul className="list-disc list-inside text-sm mt-1 max-h-32 overflow-y-auto">
                  {result.students.errors.map((error, idx) => (
                    <li key={idx} className="text-red-600">{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card de Upload */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Importar Planilha Excel
            </CardTitle>
            <CardDescription>
              Upload de arquivo .xlsx com responsáveis e alunos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted p-4 rounded-md space-y-3 text-sm">
              <p className="font-medium">O arquivo deve conter duas abas:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded text-xs font-mono">Responsaveis</span>
                  <span className="text-muted-foreground">→ Dados dos responsáveis financeiros</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-mono">Alunos</span>
                  <span className="text-muted-foreground">→ Dados dos alunos (com CPF do responsável)</span>
                </div>
              </div>
            </div>

            <Button
              variant="outline"
              onClick={downloadTemplate}
              disabled={downloading}
              className="w-full"
              data-testid="download-template-button"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Baixar Template Excel
            </Button>

            <form onSubmit={handleImport} className="space-y-4">
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <Input
                  id="excel-file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden"
                  data-testid="excel-file-input"
                />
                <label htmlFor="excel-file-input" className="cursor-pointer">
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">
                    {file ? file.name : 'Clique para selecionar arquivo'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Apenas arquivos .xlsx
                  </p>
                </label>
              </div>
              
              <Button
                type="submit"
                disabled={!file || importing}
                className="w-full"
                data-testid="import-button"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Importar Dados
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Instruções */}
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Como Importar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm">
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">1</span>
                <div>
                  <p className="font-medium">Baixe o template</p>
                  <p className="text-muted-foreground">Clique em "Baixar Template Excel" para obter a planilha modelo.</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">2</span>
                <div>
                  <p className="font-medium">Preencha os responsáveis</p>
                  <p className="text-muted-foreground">Na aba "Responsaveis", preencha os dados. Campos com * são obrigatórios.</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">3</span>
                <div>
                  <p className="font-medium">Preencha os alunos</p>
                  <p className="text-muted-foreground">Na aba "Alunos", preencha os dados. Use o CPF do responsável para vincular.</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">4</span>
                <div>
                  <p className="font-medium">Faça o upload</p>
                  <p className="text-muted-foreground">Selecione o arquivo e clique em "Importar Dados".</p>
                </div>
              </div>
              
              <div className="pt-3 border-t space-y-2">
                <p className="font-medium text-amber-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> Importante:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
                  <li>O sistema importa primeiro os responsáveis, depois os alunos</li>
                  <li>CPFs duplicados são ignorados (usa o existente)</li>
                  <li>O aluno é vinculado ao responsável pelo CPF</li>
                  <li>Matrículas duplicadas são rejeitadas</li>
                  <li>Mantenha os nomes das colunas do template</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campos do Template */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="text-lg">Campos do Template</CardTitle>
          <CardDescription>Referência rápida dos campos disponíveis</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-indigo-600" />
                Aba Responsáveis
              </h4>
              <div className="bg-muted p-3 rounded-md">
                <p className="text-xs mb-2"><span className="text-red-500">*</span> = obrigatório</p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span className="font-mono">nome*</span>
                  <span className="font-mono">cpf*</span>
                  <span className="font-mono">rg</span>
                  <span className="font-mono">rg_orgao</span>
                  <span className="font-mono">data_nascimento</span>
                  <span className="font-mono">nacionalidade</span>
                  <span className="font-mono">profissao</span>
                  <span className="font-mono">local_trabalho</span>
                  <span className="font-mono">cep</span>
                  <span className="font-mono">endereco</span>
                  <span className="font-mono">numero</span>
                  <span className="font-mono">complemento</span>
                  <span className="font-mono">bairro</span>
                  <span className="font-mono">cidade</span>
                  <span className="font-mono">estado</span>
                  <span className="font-mono">telefone</span>
                  <span className="font-mono">celular</span>
                  <span className="font-mono">whatsapp</span>
                  <span className="font-mono">email</span>
                  <span className="font-mono">parentesco</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-600" />
                Aba Alunos
              </h4>
              <div className="bg-muted p-3 rounded-md">
                <p className="text-xs mb-2"><span className="text-red-500">*</span> = obrigatório</p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span className="font-mono">nome*</span>
                  <span className="font-mono">matricula*</span>
                  <span className="font-mono">turma*</span>
                  <span className="font-mono">cpf_responsavel*</span>
                  <span className="font-mono">ano_letivo</span>
                  <span className="font-mono">situacao</span>
                  <span className="font-mono">cpf</span>
                  <span className="font-mono">rg</span>
                  <span className="font-mono">data_nascimento</span>
                  <span className="font-mono">genero</span>
                  <span className="font-mono">endereco</span>
                  <span className="font-mono">cidade</span>
                  <span className="font-mono">estado</span>
                  <span className="font-mono">telefone</span>
                  <span className="font-mono">celular</span>
                  <span className="font-mono">email</span>
                  <span className="font-mono">tipo_sanguineo</span>
                  <span className="font-mono">alergias</span>
                  <span className="font-mono">observacoes</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Tab Professores */}
        <TabsContent value="teachers" className="space-y-6 mt-6">
          {/* Resultado da Importação de Professores */}
          {teacherResult && (
            <Alert className={teacherResult.success ? 'border-green-500 bg-green-50' : 'border-amber-500 bg-amber-50'}>
              {teacherResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-amber-600" />
              )}
              <AlertDescription>
                <p className="font-semibold mb-3">{teacherResult.message}</p>
                
                <div className="bg-white/50 p-3 rounded-md mb-3">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" /> Professores
                  </p>
                  <p className="text-2xl font-bold text-green-600">{teacherResult.imported || 0}</p>
                  <p className="text-xs text-muted-foreground">importados</p>
                </div>
                
                {teacherResult.errors?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-700">Erros:</p>
                    <ul className="list-disc list-inside text-sm mt-1 max-h-32 overflow-y-auto">
                      {teacherResult.errors.map((error, idx) => (
                        <li key={idx} className="text-red-600">{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Card de Upload Professores */}
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-purple-600" />
                  Importar Professores
                </CardTitle>
                <CardDescription>
                  Upload de arquivo .xlsx com dados dos professores
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  variant="outline"
                  onClick={downloadTeacherTemplate}
                  disabled={downloadingTeachers}
                  className="w-full"
                >
                  {downloadingTeachers ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Baixar Template Professores
                </Button>

                <form onSubmit={handleImportTeachers} className="space-y-4">
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-purple-500/50 transition-colors">
                    <Input
                      id="teacher-file-input"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setTeacherFile(e.target.files[0])}
                      className="hidden"
                    />
                    <label htmlFor="teacher-file-input" className="cursor-pointer">
                      <GraduationCap className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm font-medium">
                        {teacherFile ? teacherFile.name : 'Clique para selecionar arquivo'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Apenas arquivos .xlsx
                      </p>
                    </label>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={!teacherFile || importingTeachers}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {importingTeachers ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Importar Professores
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Campos do Template de Professores */}
            <Card className="border-purple-200 bg-purple-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Campos do Template
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 text-sm">
                  <div className="bg-white p-3 rounded-md">
                    <p className="text-xs mb-2"><span className="text-red-500 font-bold">Vermelho</span> = obrigatório</p>
                    
                    <p className="font-medium text-xs mb-1 text-purple-700">Dados Pessoais:</p>
                    <div className="grid grid-cols-3 gap-1 text-xs mb-3">
                      <span className="font-mono bg-red-100 px-1 rounded">Nome Completo*</span>
                      <span className="font-mono bg-red-100 px-1 rounded">CPF*</span>
                      <span className="font-mono bg-red-100 px-1 rounded">E-mail*</span>
                      <span className="font-mono bg-purple-100 px-1 rounded">RG</span>
                      <span className="font-mono bg-purple-100 px-1 rounded">Data Nasc.</span>
                      <span className="font-mono bg-purple-100 px-1 rounded">Gênero</span>
                      <span className="font-mono bg-purple-100 px-1 rounded">Estado Civil</span>
                      <span className="font-mono bg-purple-100 px-1 rounded">Cidade Natal</span>
                    </div>
                    
                    <p className="font-medium text-xs mb-1 text-purple-700">Endereço:</p>
                    <div className="grid grid-cols-4 gap-1 text-xs mb-3">
                      <span className="font-mono bg-gray-100 px-1 rounded">Endereço</span>
                      <span className="font-mono bg-gray-100 px-1 rounded">Bairro</span>
                      <span className="font-mono bg-gray-100 px-1 rounded">Cidade</span>
                      <span className="font-mono bg-gray-100 px-1 rounded">CEP</span>
                    </div>
                    
                    <p className="font-medium text-xs mb-1 text-purple-700">Contato:</p>
                    <div className="grid grid-cols-2 gap-1 text-xs mb-3">
                      <span className="font-mono bg-gray-100 px-1 rounded">Fone Celular</span>
                      <span className="font-mono bg-gray-100 px-1 rounded">Fone Residencial</span>
                    </div>
                    
                    <p className="font-medium text-xs mb-1 text-purple-700">Dados Profissionais:</p>
                    <div className="grid grid-cols-3 gap-1 text-xs mb-3">
                      <span className="font-mono bg-gray-100 px-1 rounded">Nº Matrícula</span>
                      <span className="font-mono bg-gray-100 px-1 rounded">Cargo</span>
                      <span className="font-mono bg-gray-100 px-1 rounded">Data Admissão</span>
                      <span className="font-mono bg-gray-100 px-1 rounded">Carteira Prof.</span>
                      <span className="font-mono bg-gray-100 px-1 rounded">PIS/PASEP</span>
                      <span className="font-mono bg-gray-100 px-1 rounded">INSS</span>
                      <span className="font-mono bg-gray-100 px-1 rounded">Nº Dependentes</span>
                      <span className="font-mono bg-gray-100 px-1 rounded">Currículo</span>
                    </div>
                    
                    <p className="font-medium text-xs mb-1 text-purple-700">Vínculo:</p>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <span className="font-mono bg-gray-100 px-1 rounded">Disciplina</span>
                      <span className="font-mono bg-gray-100 px-1 rounded">Turma</span>
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t space-y-2">
                    <p className="font-medium text-purple-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> Observações:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
                      <li>A senha do professor será o CPF (apenas números)</li>
                      <li>Se a disciplina existir, o professor será vinculado</li>
                      <li>Se a turma existir, o vínculo será criado automaticamente</li>
                      <li>CPFs duplicados atualizam os dados existentes</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
